/**
 * @file routes.js
 * @description Centralized application route constants.
 */

export const ROUTES = {
    ROOT: '/',
    LOGIN: '/login',
    ABOUT: '/about',
    BRANDING: '/branding',
    PROFILE: '/profile',
    USER_INFO: '/user-info',
    PASSWORD: '/password',
    COMMUNICATION_PREFERENCES: '/communication-preferences',
    INSTALL_APP: '/install-app',
    PRIVACY_POLICY: '/privacy-policy',
    CONTACT_SUPPORT: '/contact-support',
    GET_SUPPORT: '/get-support',
    FAQS: '/faqs',

    VERIFY_MFA: '/verify-mfa/:token',
    VERIFY_MFA_WILDCARD: '/verify-mfa/*',
    FORGOT_PASSWORD: '/forgot-password',
    FORGOT_EMAIL: '/forgot-email',
    RESET_PASSWORD_USER: '/reset-password/reset-user/:id',
    RESET_PASSWORD_PREFIX: '/reset-password/reset-user',
    RESET_PASSWORD_WILDCARD: '/reset-password/*',

    ONBOARDING: '/onboarding/:onboardingCode',
    ONBOARDING_SET_USERNAME: '/onboarding/:onboardingCode/set-username',
    ONBOARDING_FIND_USER: '/onboarding/anonymous/find-user',
    ONBOARDING_WILDCARD: '/onboarding/*',

    ACTIONS: '/actions',
    ACTIONS_VIEW: '/actions/view/:actionId',

    GROUPS: '/groups',
    GROUPS_NEW: '/groups/new',
    GROUPS_EDIT: '/groups/:groupId/edit',
    INDIVIDUALS: '/individuals',
    INDIVIDUALS_VIEW: '/individuals/view/:individualId',
    INDIVIDUALS_ADD: '/individuals/add',

    PORTAL_PAGES: '/portal-pages',
    PORTAL_PAGES_NEW: '/portal-pages/new-portal-page',
    PORTAL_PAGES_ARRANGE_TABS: '/portal-pages/arrange-portal-tabs',
    PORTAL_PAGES_VIEW: '/portal-pages/:portalId',

    PORTAL_TEMPLATES: '/portal-templates',
    PORTAL_TEMPLATE_PREVIEW: '/portal-templates/preview/:portalId',
    PORTAL_TEMPLATES_NEW: '/portal-templates/new-template',

    TEMPLATES: '/templates',
    TEMPLATES_VIEW: '/templates/view',
    TEMPLATES_NEW: '/templates/new-template',
    UPDATED_PORTAL_WIZARD: '/updated-portal-wizard',

    NOTIFICATION_TEMPLATES: '/notification-templates',
    NOTIFICATION_TEMPLATES_NEW: '/notification-templates/new-template',
    NOTIFICATION_TEMPLATES_VIEW: '/notification-templates/view/:notificationId',
    NOTIFICATION_SCHEDULE_LIST: '/messages/view/:notificationId/schedules',
    NOTIFICATION_SCHEDULE_NEW: '/messages/view/:notificationId/schedule/new',
    NOTIFICATION_SCHEDULE_VIEW:
        '/messages/view/:notificationId/schedule/:scheduleDefinitionId',
    NOTIFICATION_SCHEDULE_HISTORY:
        '/messages/view/:notificationId/schedule/:scheduleDefinitionId/history',

    ORG_LEVELS: '/org-levels/:orgLevelType',
    ORG_LEVELS_EDIT: '/org-levels/:orgLevelType/:orgLevelID',
    ORG_LEVELS_DEPARTMENTS: '/org-levels/departments',
    ORG_LEVELS_DIVISIONS: '/org-levels/divisions',
    ORG_LEVELS_LOCATIONS: '/org-levels/locations',
    ORG_LEVELS_REGIONS: '/org-levels/regions',
    ORG_LEVELS_BUSINESS_UNITS: '/org-levels/business-units',
    ORG_LEVELS_PAY_GROUPS: '/org-levels/pay-groups',

    NEW_FILE_UPLOAD: '/new-file-upload',
    IMPORT_QUEUE: '/import-queue',
    CUSTOM_IMPORTS: '/custom-imports',
    AUTO_IMPORTS: '/auto-imports',
    AUTO_IMPORTS_EDIT: '/auto-imports/edit/:autoImportId',
    AUTO_IMPORTS_EDIT_ADD: '/auto-imports/edit/add',

    SECURITY_ROLES: '/security-roles',
    SECURITY_ROLES_VIEW: '/security-roles/view/:roleId',
    SECURITY_ROLES_EDIT: '/security-roles/edit-role/:roleId',
    SECURITY_ROLES_ADD_USER: '/security-roles/add-user/:roleId',
    SECURITY_ROLES_ADD: '/security-roles/add',

    VENDORS: '/vendors',
    VENDORS_ADD: '/vendors/add',
    VENDORS_EDIT: '/vendors/edit/:vendorId',

    PLAN_DETAILS: '/plan-details',
    PLAN_DETAILS_ADD: '/plan-details/add',
    PLAN_DETAILS_EDIT: '/plan-details/edit/:planDetailId',

    THEME: '/theme',
    EVENT_LOG: '/event-log',

    SSO_LIST: '/sso-list',
    SSO_CLIENT_VIEW: '/sso-client/view/:ssoClientId',
    SSO_CLIENT_ADD: '/sso-client/add',
    SSO_MULTI_ADD: '/sso-multi/add',
    SSO_MULTI_VIEW: '/sso-multi/view/:ssoMultiId',
    SSO_LINK: '/sso-link/:tenantSSOId',

    SETTINGS: '/settings',

    AGENCIES: '/agency-list',
    AGENCIES_VIEW: '/agency/view/:agencyId',

    USER_ACCOUNTS: '/user-accounts',
    USER_ACCOUNTS_VIEW: '/user-accounts/view/:userAccountId',
    USER_ACCOUNTS_ADD: '/user-accounts/add',

    ADDITIONAL_USERS: '/additional-users',
    ADDITIONAL_USERS_ADD: '/additional-users/add',
    ADDITIONAL_USERS_VIEW: '/additional-users/view/:userId',

    CLIENTS: '/clients',
    CLIENTS_VIEW: '/clients/view/:clientId',
    CLIENTS_ADD: '/clients/add',
    CLIENTS_ADD_CONTACT: '/clients/view/:clientId/add-contact',
    CLIENTS_UPDATE_CONTACT: '/clients/view/:clientId/update-contact/:contactId',
    CLIENTS_ADD_USER: '/clients/view/:clientId/add-user',

    BILLING_CLIENTS: '/billing/clients',
    BILLING_CLIENTS_VIEW: '/billing/clients/view/:clientId',
    BILLING_SMS_REPORT: '/billing/sms-report',

    CHARTS: '/charts',
    MESSAGES: '/messages',
    MESSAGES_VIEW: '/messages/view/:messageId',
    MESSAGES_ADD: '/messages/add',
    MY_MESSAGES: '/my-messages',
    MY_MESSAGES_VIEW: '/my-messages/:notificationId',
    MY_MESSAGES_WITH_OPTIONAL_ID: '/my-messages/:notificationId?',
    MESSAGES_HISTORY: '/messages-history',
    MESSAGES_HISTORY_DETAILS: '/messages-history/view/:scheduleId',
    MESSAGES_TEMPLATES: '/messages/templates',
    MESSAGES_TEMPLATES_VIEW: '/messages/templates/view/:notificationId',

    ASSETS: '/assets',
    ASSETS_NEW: '/assets/new-asset',
    GLOBAL_ASSETS: '/global-assets',
    GLOBAL_ASSETS_NEW: '/global-assets/new-asset',
    MY_ASSETS: '/my-assets',
    MY_ASSETS_NEW: '/my-assets/new-asset',
    RESOURCES: '/resources',
    RESOURCES_NEW: '/resources/new-resource',
    RESOURCE_VIEWER: '/resource-viewer/:assetId',
    ASSET_VIEWER: '/asset-viewer/:assetId',
    HQ_TRAINING: '/hq-training',

    DOCUMENT_VIEWER: '/document-viewer',

    MY_BENEFITS: '/my-benefits',
    MY_BENEFITS_PLAN_DETAILS: '/my-benefits/plan-details/:planId/:planYearId',
    VIEW_BENEFITS: '/view-benefits',
    // Individual-side entry point for the home page, used by links that leave the app.
    MY_HOME: '/my-home',

    BENEFIT_PLANS: '/benefit-plans',
    BENEFIT_PLANS_VIEW: '/benefit-plans/view/:benefitPlanId',
    BENEFIT_PLANS_ADD: '/benefit-plans/add',

    PLAN_YEAR: '/plan-year',
    PLAN_YEAR_VIEW: '/plan-year/view/:planYearId',
    PLAN_YEAR_ADD: '/plan-year/add',

    PLAN_TYPES: '/plan-types',
    PLAN_TYPES_ARRANGE: '/plan-types/arrange',
    PLAN_TYPE_CATEGORIES: '/plan-types/:planTypeId/categories',
    PLAN_TYPE_CATEGORY_ITEM: '/plan-types/:planTypeId/categories/:categoryId',

    MULTITENANT_IMPORT_QUEUE: '/multitenant-import-queue',

    VIEW_LIVE: '/view-live',
    VIEW_LIVE_PORTAL: '/view-live/:portalId',

    REPORTS_LIST: '/reports/list',
    REPORTS_HISTORY: '/reports/history',
    REPORTS_ALL: '/reports/all',
    REPORTS_GENERATE: '/reports/:reportId/generate',
    REPORTS_VIEW: '/reports/view/:savedReportId',

    DATA_POINTS: '/data-points',
    DATA_POINTS_NEW: '/data-points/new',
    DATA_POINTS_EDIT: '/data-points/:categoryId/edit',
    DATA_POINTS_SUBCATEGORY: '/data-points/:categoryId/sub-categories',
    DATA_POINTS_SUBCATEGORY_NEW: '/data-points/:categoryId/sub-categories/new',
    DATA_POINTS_SUBCATEGORY_EDIT:
        '/data-points/:categoryId/sub-categories/:subCategoryId/edit',

    NOT_FOUND: '*',
};

function withParam(routePattern, paramName, value) {
    return routePattern.replace(`:${paramName}`, String(value));
}

function withQuery(route, query) {
    return query ? `${route}?${query}` : route;
}

export const ROUTE_BUILDERS = {
    loginWithCompanyCode: companyCode =>
        companyCode
            ? `${ROUTES.LOGIN}?companyCode=${encodeURIComponent(companyCode)}`
            : ROUTES.LOGIN,
    onboardingFindUserWithCompanyCode: companyCode =>
        companyCode
            ? `${ROUTES.ONBOARDING_FIND_USER}?companyCode=${encodeURIComponent(companyCode)}`
            : ROUTES.ONBOARDING_FIND_USER,
    forgotPasswordResetExpired: companyCode =>
        `${ROUTES.FORGOT_PASSWORD}?companyCode=${encodeURIComponent(companyCode)}&resetLinkExpired=true`,
    getSupportWithCompanyCode: companyCode =>
        companyCode
            ? `${ROUTES.GET_SUPPORT}?companyCode=${encodeURIComponent(companyCode)}`
            : ROUTES.GET_SUPPORT,
    loginWithRawQueryString: searchString =>
        withQuery(ROUTES.LOGIN, searchString),
    onboardingWithCompanyCode: (onboardingCode, companyCode) =>
        `${withParam(ROUTES.ONBOARDING, 'onboardingCode', onboardingCode)}?companyCode=${encodeURIComponent(companyCode)}`,

    clientsView: clientId =>
        withParam(ROUTES.CLIENTS_VIEW, 'clientId', clientId),
    billingClientsView: clientId =>
        withParam(ROUTES.BILLING_CLIENTS_VIEW, 'clientId', clientId),
    userAccountsView: userAccountId =>
        withParam(ROUTES.USER_ACCOUNTS_VIEW, 'userAccountId', userAccountId),
    securityRolesView: roleId =>
        withParam(ROUTES.SECURITY_ROLES_VIEW, 'roleId', roleId),
    securityRolesEdit: roleId =>
        withParam(ROUTES.SECURITY_ROLES_EDIT, 'roleId', roleId),
    securityRolesAddUser: roleId =>
        withParam(ROUTES.SECURITY_ROLES_ADD_USER, 'roleId', roleId),
    portalPagesView: portalId =>
        withParam(ROUTES.PORTAL_PAGES_VIEW, 'portalId', portalId),
    viewLivePortal: portalId =>
        withParam(ROUTES.VIEW_LIVE_PORTAL, 'portalId', portalId),
    ssoLink: tenantSSOId =>
        withParam(ROUTES.SSO_LINK, 'tenantSSOId', tenantSSOId),
    reportsGenerate: reportId =>
        withParam(ROUTES.REPORTS_GENERATE, 'reportId', reportId),
    reportsView: savedReportId =>
        withParam(ROUTES.REPORTS_VIEW, 'savedReportId', savedReportId),
    reportsGenerateReadOnly: savedReportId =>
        withParam(ROUTES.REPORTS_VIEW, 'savedReportId', savedReportId),
    actionsView: actionId =>
        withParam(ROUTES.ACTIONS_VIEW, 'actionId', actionId),
    orgLevels: orgLevelType =>
        withParam(ROUTES.ORG_LEVELS, 'orgLevelType', orgLevelType),
    myBenefitsPlanDetails: (planId, planYearId) =>
        withParam(
            withParam(ROUTES.MY_BENEFITS_PLAN_DETAILS, 'planId', planId),
            'planYearId',
            planYearId,
        ),
    autoImportEdit: autoImportId =>
        withParam(ROUTES.AUTO_IMPORTS_EDIT, 'autoImportId', autoImportId),
    benefitPlansView: benefitPlanId =>
        withParam(ROUTES.BENEFIT_PLANS_VIEW, 'benefitPlanId', benefitPlanId),
    planYearsView: planYearId =>
        withParam(ROUTES.PLAN_YEAR_VIEW, 'planYearId', planYearId),
    planTypeCategories: planTypeId =>
        withParam(ROUTES.PLAN_TYPE_CATEGORIES, 'planTypeId', planTypeId),
    planTypeCategoryItem: (planTypeId, categoryId) =>
        withParam(
            withParam(ROUTES.PLAN_TYPE_CATEGORY_ITEM, 'planTypeId', planTypeId),
            'categoryId',
            categoryId,
        ),
    agenciesView: agencyId =>
        withParam(ROUTES.AGENCIES_VIEW, 'agencyId', agencyId),
    vendorsEdit: vendorId =>
        withParam(ROUTES.VENDORS_EDIT, 'vendorId', vendorId),
    planDetailsEdit: planDetailId =>
        withParam(ROUTES.PLAN_DETAILS_EDIT, 'planDetailId', planDetailId),
    portalTemplatesPreview: portalId =>
        withParam(ROUTES.PORTAL_TEMPLATE_PREVIEW, 'portalId', portalId),
    messagesView: messageId =>
        withParam(ROUTES.MESSAGES_VIEW, 'messageId', messageId),
    myMessagesView: notificationId =>
        withParam(ROUTES.MY_MESSAGES_VIEW, 'notificationId', notificationId),
    messagesHistoryDetails: scheduleId =>
        withParam(ROUTES.MESSAGES_HISTORY_DETAILS, 'scheduleId', scheduleId),
    notificationTemplatesView: notificationId =>
        withParam(
            ROUTES.NOTIFICATION_TEMPLATES_VIEW,
            'notificationId',
            notificationId,
        ),
    notificationScheduleList: notificationId =>
        withParam(
            ROUTES.NOTIFICATION_SCHEDULE_LIST,
            'notificationId',
            notificationId,
        ),
    notificationScheduleNew: notificationId =>
        withParam(
            ROUTES.NOTIFICATION_SCHEDULE_NEW,
            'notificationId',
            notificationId,
        ),
    notificationScheduleView: (notificationId, scheduleDefinitionId) =>
        withParam(
            withParam(
                ROUTES.NOTIFICATION_SCHEDULE_VIEW,
                'notificationId',
                notificationId,
            ),
            'scheduleDefinitionId',
            scheduleDefinitionId,
        ),
    notificationScheduleHistory: (notificationId, scheduleDefinitionId) =>
        withParam(
            withParam(
                ROUTES.NOTIFICATION_SCHEDULE_HISTORY,
                'notificationId',
                notificationId,
            ),
            'scheduleDefinitionId',
            scheduleDefinitionId,
        ),
    messagesTemplatesView: notificationId =>
        withParam(
            ROUTES.MESSAGES_TEMPLATES_VIEW,
            'notificationId',
            notificationId,
        ),
    ssoMultiView: ssoMultiId =>
        withParam(ROUTES.SSO_MULTI_VIEW, 'ssoMultiId', ssoMultiId),
    ssoClientView: ssoClientId =>
        withParam(ROUTES.SSO_CLIENT_VIEW, 'ssoClientId', ssoClientId),
    dataPointsSubCategory: categoryId =>
        withParam(ROUTES.DATA_POINTS_SUBCATEGORY, 'categoryId', categoryId),
    dataPointsEdit: categoryId =>
        withParam(ROUTES.DATA_POINTS_EDIT, 'categoryId', categoryId),
    dataPointsSubCategoryNew: categoryId =>
        withParam(ROUTES.DATA_POINTS_SUBCATEGORY_NEW, 'categoryId', categoryId),
    dataPointsSubCategoryEdit: (categoryId, subCategoryId) =>
        withParam(
            withParam(
                ROUTES.DATA_POINTS_SUBCATEGORY_EDIT,
                'categoryId',
                categoryId,
            ),
            'subCategoryId',
            subCategoryId,
        ),
};
